import axios from "axios";
import {
  all,
  call as RSCall,
  put,
  takeLatest,
  takeEvery,
} from "redux-saga/effects";

import { IInitialState } from "@/types/initialState.types";
import { showToast } from "@/actions/appStatus.action";
import {
  getSubjectiveSolutionByQuestionSlugAPI,
  createOrUpdateSubjectiveSolutionAPI,
  updateSubjectiveSolutionAPI,
  uploadAttachmentAPI,
  deleteAttachmentAPI,
} from "@/api/solutions.api";
import {
  GET_SUBJECTIVE_SOLUTION,
  CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION,
  SET_SUBJECTIVE_ATTACHMENTS,
  GET_SUBJECTIVE_ATTACHMENTS,
  UPLOAD_SUBJECTIVE_ATTACHMENTS,
  DELETE_SUBJECTIVE_ATTACHMENT,
} from "@/constants/ActionTypes";
import { HTTP_STATUS_CODES } from "@/constants/HttpConstants";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import { encodeData } from "@/utils/encodeDecode.utils";
import {
  INTERVIEW_SOLUTION_CREATED,
  SUBJECTIVE_ATTACHMENTS_CHANGE,
} from "@/constants/SocketEventConstants";
import { EUserRole } from "@/types/auth.types";
import { getServerTime } from "@/utils/utils";
import getSolutionSlugFromCreateResponse from "@/utils/solution.utils";
import { call, select } from "./utils.saga";

interface IGetSubjectiveSolutionAction {
  type: string;
  payload: {
    interviewSlug: string;
    questionSlug: string;
  };
}

interface ICreateOrUpdateSubjectiveSolutionAction {
  type: string;
  payload: {
    interviewSlug: string;
    questionSlug: string;
  };
}

function* getSubjectiveSolution({ payload }: IGetSubjectiveSolutionAction) {
  const { interviewSlug, questionSlug } = payload;
  try {
    if (!interviewSlug || !questionSlug) return;
    const serverTimeOffsetTime = yield* select(
      (state: IInitialState) => state.serverTime.offsetTime,
    );
    const resp = yield* call(
      getSubjectiveSolutionByQuestionSlugAPI,
      interviewSlug,
      questionSlug,
    );
    if (resp.status === HTTP_STATUS_CODES.OK && resp.data) {
      yield put({
        type: `${GET_SUBJECTIVE_SOLUTION}_SUCCESS`,
        payload: {
          questionSlug,
          solution: {
            slug: resp.data.slug,
            content: resp.data.answer, // API uses `answer`, frontend uses `content`
            attachments: resp.data.attachments ?? [],
            modifiedAt: getServerTime(serverTimeOffsetTime),
          },
        },
      });
    }
  } catch (error: unknown) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === HTTP_STATUS_CODES.NOT_FOUND
    ) {
      yield put({
        type: `${GET_SUBJECTIVE_SOLUTION}_SUCCESS`,
        payload: {
          questionSlug,
          solution: {
            slug: undefined,
            content: "",
            attachments: [],
            modifiedAt: undefined,
          },
        },
      });
      return;
    }
    yield put(
      showToast({
        message: "Failed to load subjective solution.",
        type: EToastType.ERROR,
      }),
    );
  }
}

function* createOrUpdateSubjectiveSolution({
  payload,
}: ICreateOrUpdateSubjectiveSolutionAction) {
  const { interviewSlug, questionSlug } = payload;
  try {
    if (!interviewSlug || !questionSlug) return;

    const { solution, socket } = yield* select((state: IInitialState) => ({
      solution: state.solutions.subjectiveSolutions[questionSlug],
      socket: state.socket.socket,
    }));

    if (!solution) return;

    const requestPayload = {
      solution_type: "SUB" as const,
      question_slug: questionSlug,
      answer: solution.content, // API uses `answer`, frontend uses `content`
    };

    if (!solution.slug) {
      const createResp = yield* call(
        createOrUpdateSubjectiveSolutionAPI,
        interviewSlug,
        requestPayload,
      );
      const solutionSlug = getSolutionSlugFromCreateResponse(createResp.data);
      socket?.emit(INTERVIEW_SOLUTION_CREATED, encodeData({ questionSlug }));
      yield put({
        type: `${CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION}_SUCCESS`,
        payload: { questionSlug, slug: solutionSlug },
      });
    } else {
      yield* call(
        updateSubjectiveSolutionAPI,
        interviewSlug,
        solution.slug,
        requestPayload,
      );
    }
  } catch (error: unknown) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === HTTP_STATUS_CODES.NOT_FOUND
    )
      return;
    yield put(
      showToast({
        message: "Failed to save subjective solution.",
        type: EToastType.ERROR,
      }),
    );
  }
}

interface IUploadSubjectiveAttachmentsAction {
  type: string;
  payload: {
    interviewSlug: string;
    questionSlug: string;
    files: File[];
  };
}

interface IDeleteSubjectiveAttachmentAction {
  type: string;
  payload: {
    interviewSlug: string;
    questionSlug: string;
    fileName: string;
  };
}

// Fetch the latest attachments for a question and store them. GET solution is
// the single source of truth — the upload response only echoes the file just
// uploaded (and carries no usable URL), so we never build the list from it.
function* refetchAttachments(interviewSlug: string, questionSlug: string) {
  const resp = yield* call(
    getSubjectiveSolutionByQuestionSlugAPI,
    interviewSlug,
    questionSlug,
  );
  if (resp.status === HTTP_STATUS_CODES.OK && resp.data) {
    yield put({
      type: SET_SUBJECTIVE_ATTACHMENTS,
      payload: { questionSlug, attachments: resp.data.attachments ?? [] },
    });
  }
}

function* uploadSubjectiveAttachments({
  payload,
}: IUploadSubjectiveAttachmentsAction) {
  const { interviewSlug, questionSlug, files } = payload;
  try {
    if (!interviewSlug || !questionSlug || !files.length) return;

    const { solution, socket, userIdentityData } = yield* select(
      (state: IInitialState) => ({
        solution: state.solutions.subjectiveSolutions[questionSlug],
        socket: state.socket.socket,
        userIdentityData: state.auth.userIdentityData,
      }),
    );

    // The backend keys attachments by solution_slug, so a solution must exist
    // before we can upload. The button is available upfront (even before the
    // candidate types anything), so create the solution on demand here when it
    // doesn't exist yet, then upload against the new slug.
    let solutionSlug = solution?.slug;
    if (!solutionSlug) {
      const createResp = yield* call(
        createOrUpdateSubjectiveSolutionAPI,
        interviewSlug,
        {
          solution_type: "SUB" as const,
          question_slug: questionSlug,
          answer: solution?.content ?? "",
        },
      );
      solutionSlug = getSolutionSlugFromCreateResponse(createResp.data);
      socket?.emit(INTERVIEW_SOLUTION_CREATED, encodeData({ questionSlug }));
      yield put({
        type: `${CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION}_SUCCESS`,
        payload: { questionSlug, slug: solutionSlug },
      });
    }

    // Never call the attachment API without a real slug — the backend keys
    // attachments by solution_slug, and sending an undefined slug just fails.
    if (!solutionSlug) {
      throw new Error("Solution slug unavailable after create");
    }

    // Upload all selected files concurrently, then refetch the full solution.
    yield all(
      files.map((file) =>
        RSCall(uploadAttachmentAPI, interviewSlug, solutionSlug, file),
      ),
    );
    yield put({
      type: GET_SUBJECTIVE_SOLUTION,
      payload: { interviewSlug, questionSlug },
    });

    // Notify the other participant; they refetch attachments via GET solution.
    socket?.emit(
      SUBJECTIVE_ATTACHMENTS_CHANGE,
      encodeData({
        questionSlug,
        senderInfo: {
          name: userIdentityData?.name ?? "",
          email: userIdentityData?.email ?? "",
          role: userIdentityData?.role ?? EUserRole.CANDIDATE,
        },
      }),
    );
  } catch {
    yield put(
      showToast({
        message: "Failed to upload attachment.",
        type: EToastType.ERROR,
      }),
    );
  }
}

function* deleteSubjectiveAttachment({
  payload,
}: IDeleteSubjectiveAttachmentAction) {
  const { interviewSlug, questionSlug, fileName } = payload;
  try {
    if (!interviewSlug || !questionSlug || !fileName) return;

    const { solution, socket, userIdentityData } = yield* select(
      (state: IInitialState) => ({
        solution: state.solutions.subjectiveSolutions[questionSlug],
        socket: state.socket.socket,
        userIdentityData: state.auth.userIdentityData,
      }),
    );

    if (!solution?.slug) return;

    yield* call(deleteAttachmentAPI, interviewSlug, solution.slug, fileName);

    // Refetch the full solution so the stored state reflects the server.
    yield put({
      type: GET_SUBJECTIVE_SOLUTION,
      payload: { interviewSlug, questionSlug },
    });

    // Notify the other participant; they refetch attachments via GET solution.
    socket?.emit(
      SUBJECTIVE_ATTACHMENTS_CHANGE,
      encodeData({
        questionSlug,
        senderInfo: {
          name: userIdentityData?.name ?? "",
          email: userIdentityData?.email ?? "",
          role: userIdentityData?.role ?? EUserRole.CANDIDATE,
        },
      }),
    );
  } catch {
    yield put(
      showToast({
        message: "Failed to remove attachment.",
        type: EToastType.ERROR,
      }),
    );
  }
}

// Refetch only the attachments for a question (used when another participant
// changes attachments). Reuses the GET solution API but deliberately updates
// only `attachments` so the live editor content is never clobbered.
function* getSubjectiveAttachments({ payload }: IGetSubjectiveSolutionAction) {
  const { interviewSlug, questionSlug } = payload;
  try {
    if (!interviewSlug || !questionSlug) return;
    yield* refetchAttachments(interviewSlug, questionSlug);
  } catch (error: unknown) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === HTTP_STATUS_CODES.NOT_FOUND
    )
      return;
    yield put(
      showToast({
        message: "Failed to load attachments.",
        type: EToastType.ERROR,
      }),
    );
  }
}

export default function* loadSubjectiveSolutionSaga() {
  yield takeLatest(GET_SUBJECTIVE_SOLUTION, getSubjectiveSolution);
  yield takeLatest(
    CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION,
    createOrUpdateSubjectiveSolution,
  );
  yield takeEvery(GET_SUBJECTIVE_ATTACHMENTS, getSubjectiveAttachments);
  yield takeEvery(UPLOAD_SUBJECTIVE_ATTACHMENTS, uploadSubjectiveAttachments);
  yield takeEvery(DELETE_SUBJECTIVE_ATTACHMENT, deleteSubjectiveAttachment);
}
